/**
 * Shared misspelling plugin utilities for BlockNote editors
 * Centralized implementation extracted from NoteSection component
 */

import type { BlockNoteEditor } from '@blocknote/core';

import {
  createMisspellingPlugin,
  misspellingPluginKey,
} from '../../components/BlockNoteMisspellingPlugin';

export {
  createMisspellingPlugin,
  misspellingPluginKey,
  updateMisspellingPlugin,
} from '../../components/BlockNoteMisspellingPlugin';

/**
 * Applies the misspelling plugin to a BlockNote editor instance
 * @param editor The BlockNote editor instance
 * @param misspelledWords Array of misspelled words to highlight
 * @param onWordClick Optional callback when a misspelled word is clicked
 * @param troubleWords Optional array of trouble words with correct spellings
 */
export const applyMisspellingPlugin = (
  editor: BlockNoteEditor,
  misspelledWords: string[],
  onWordClick?: (word: string) => void,
  troubleWords?: Array<{ correct_spelling: string; misspellings: string[] }>,
): void => {
  // Access the underlying TipTap/ProseMirror instance
  // BlockNote uses TipTap under the hood
  // This requires accessing internal APIs, so we disable linting for this section
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
  const tiptapEditor = (editor as any)._tiptapEditor;
  if (!tiptapEditor || !tiptapEditor.view) {
    console.warn('BlockNote editor TipTap instance not available');
    return;
  }

  try {
    const state = tiptapEditor.state;

    // Helper function to check if a plugin is a misspelling plugin
    // ProseMirror may append characters like '$' to plugin keys, so we check if the key string starts with our identifier
    const isMisspellingPlugin = (plugin: any): boolean => {
      if (!plugin || !plugin.key) return false;
      
      // Check by PluginKey object reference
      if (plugin.key === misspellingPluginKey) return true;
      
      // Check by key string identifier
      const keyString = plugin.key?.toString() || '';
      if (keyString === misspellingPluginKey.toString() || keyString.startsWith('misspelling-highlight')) {
        return true;
      }
      
      // Check if key.toString() starts with our identifier (handles cases like "misspelling-highlight$")
      const keyStr = String(plugin.key);
      if (keyStr.startsWith('misspelling-highlight')) {
        return true;
      }
      
      return false;
    };

    // Filter out any existing misspelling plugin using robust key matching
    const existingPlugins = state.plugins.filter((p: any) => !isMisspellingPlugin(p));

    // Only create and apply plugin if there are words to highlight
    // This reduces unnecessary plugin reconfigurations
    const hasWordsToHighlight = misspelledWords && misspelledWords.length > 0;
    
    if (hasWordsToHighlight) {
      // Create new plugin
      const plugin = createMisspellingPlugin(misspelledWords, onWordClick, troubleWords);

      // Create new state with updated plugins
      const newState = state.reconfigure({
        plugins: [...existingPlugins, plugin],
      });

      tiptapEditor.view.updateState(newState);
    } else {
      // If no words to highlight, just remove any existing plugin without adding a new one
      if (existingPlugins.length !== state.plugins.length) {
        const newState = state.reconfigure({
          plugins: existingPlugins,
        });
        tiptapEditor.view.updateState(newState);
      }
    }
  } catch (error) {
    console.error('Error applying misspelling plugin:', error);
    // Log plugin keys for debugging (temporary)
    try {
      const state = (tiptapEditor).state;
      const pluginKeys = state.plugins.map((p: any) => {
        const key = p.key;
        return {
          keyObject: key,
          keyString: key?.key || key?.toString() || String(key),
          keyType: typeof key,
        };
      });
      console.error('Current plugin keys:', pluginKeys);
    } catch (logError) {
      // Ignore logging errors
    }
  }
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
};
